import React, { useEffect, useState, useRef } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import BackButton from '../components/BackButton';
import { Heart, Shield, Users, Globe, Target, Sparkles, ArrowRight, CheckCircle, Network } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const useInView = (threshold = 0.1) => {
  const [isInView, setIsInView] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setIsInView(true); }, { threshold });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, isInView];
};

const EngagementsESSPage = () => {
  const [heroRef, heroInView] = useInView();
  const [engagementsRef, engagementsInView] = useInView();
  const [principesRef, principesInView] = useInView();

  const engagements = [
    { icon: Heart, title: 'Solidarité coopérative', desc: 'Chaque action sert l\'intérêt collectif. La solidarité est au cœur de notre modèle économique.', gradient: 'from-red-500 to-rose-500' },
    { icon: Target, title: 'Mutualisation des ressources', desc: 'Nous transformons les contraintes des TPE en leviers de croissance collective.', gradient: 'from-blue-500 to-cyan-500' },
    { icon: Users, title: 'Développement des compétences', desc: 'FORMA\'SCOP forme les professionnels de demain avec des parcours adaptés.', gradient: 'from-purple-500 to-pink-500' },
    { icon: Shield, title: 'Sécurisation économique', desc: 'O\'SCOP GE protège les entreprises et les salariés face aux aléas.', gradient: 'from-green-500 to-emerald-500' },
    { icon: Globe, title: 'Impact territorial', desc: 'Nous contribuons à la résilience, la souveraineté et l\'autonomie des Outre-mer.', gradient: 'from-amber-500 to-orange-500' },
    { icon: Sparkles, title: 'Innovation sociale', desc: 'Créer de nouveaux modèles pour une économie plus juste et inclusive.', gradient: 'from-indigo-500 to-purple-500' },
  ];

  const principes = [
    { title: 'Gouvernance démocratique', desc: 'Une voix par membre, décisions collectives' },
    { title: 'Lucrativité limitée', desc: 'Les bénéfices servent le projet collectif' },
    { title: 'Utilité sociale', desc: 'Impact positif sur la société et l\'environnement' },
    { title: 'Ancrage territorial', desc: 'Développement local et circuits courts' },
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      <Navigation />
      
      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-[80vh] flex items-center overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 pt-20">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 -left-32 w-[600px] h-[600px] bg-gradient-to-r from-emerald-600/20 to-green-600/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 -right-32 w-[500px] h-[500px] bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}} />
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-20">
          <BackButton to="/" label="Retour à l'accueil" className="text-white/70 mb-8 hover:text-white transition-colors" />
          
          <div className={`transition-all duration-1000 ${heroInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Badge className="bg-gradient-to-r from-emerald-500/20 to-green-500/20 text-emerald-300 border border-emerald-500/30 mb-6 px-4 py-2">
              <Heart className="w-4 h-4 mr-2" /> Économie Sociale et Solidaire
            </Badge>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black mb-6">
              <span className="bg-gradient-to-r from-white via-emerald-100 to-white bg-clip-text text-transparent">Nos Engagements</span>
              <br />
              <span className="text-3xl md:text-4xl lg:text-5xl bg-gradient-to-r from-emerald-400 to-green-400 bg-clip-text text-transparent">ESS*</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-slate-300 max-w-4xl mb-10 leading-relaxed">
              Une économie qui place <span className="text-emerald-400 font-semibold">l'humain et la solidarité</span> au cœur 
              de son fonctionnement, pour un développement durable des territoires ultramarins.
            </p>

            <p className="text-sm text-slate-500 mb-10">*Économie Sociale et Solidaire - Loi ESS 2014</p>

            <Button size="lg" className="bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white font-bold px-8 py-6 text-lg shadow-2xl shadow-emerald-500/25 hover:shadow-emerald-500/40 transition-all duration-300 hover:scale-105" onClick={() => window.location.href = '/adhesion'}>
              Rejoindre le mouvement <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Engagements Section */}
      <section ref={engagementsRef} className="py-24 bg-gradient-to-b from-slate-950 to-slate-900 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`text-center mb-16 transition-all duration-1000 ${engagementsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Badge className="bg-purple-500/20 text-purple-300 border border-purple-500/30 mb-4 px-4 py-2"><Sparkles className="w-4 h-4 mr-2" /> Nos 6 engagements</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Une vision <span className="text-purple-400">solidaire</span></h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {engagements.map((item, index) => (
              <div key={index} className={`group bg-slate-800/30 backdrop-blur-xl rounded-2xl p-8 border border-slate-700/50 hover:border-slate-600 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl ${engagementsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: `${index * 100}ms` }}>
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${item.gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-lg`}>
                  <item.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                <p className="text-slate-400 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Principes ESS Section */}
      <section ref={principesRef} className="py-24 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0"><div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-gradient-to-b from-emerald-600/10 to-transparent rounded-full blur-3xl" /></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className={`text-center mb-16 transition-all duration-1000 ${principesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Badge className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 mb-4 px-4 py-2"><Network className="w-4 h-4 mr-2" /> Principes fondateurs</Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Les piliers de <span className="text-emerald-400">l'ESS</span></h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {principes.map((item, index) => (
              <div key={index} className={`bg-slate-800/50 backdrop-blur-xl rounded-2xl p-6 border border-slate-700/50 text-center hover:border-emerald-500/50 transition-all duration-300 ${principesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: `${index * 100}ms` }}>
                <div className="w-12 h-12 mx-auto rounded-xl bg-gradient-to-br from-emerald-500/20 to-green-500/20 flex items-center justify-center mb-4 border border-emerald-500/30">
                  <CheckCircle className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                <p className="text-sm text-slate-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-emerald-600 via-green-600 to-emerald-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.1)_1px,transparent_1px)] bg-[size:40px_40px]" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Rejoignez une <span className="text-emerald-200">économie différente</span></h2>
          <p className="text-xl text-white/80 mb-10">Ensemble, construisons un modèle économique plus juste et plus solidaire.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-emerald-600 hover:bg-slate-100 font-bold px-10 py-6 text-lg shadow-2xl transition-all duration-300 hover:scale-105" onClick={() => window.location.href = '/adhesion'}>
              Devenir membre <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-10 py-6 text-lg transition-all duration-300" onClick={() => window.location.href = '/contact'}>
              Nous contacter
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EngagementsESSPage;
